import React, { useState, useEffect, useContext, useMemo } from 'react';

import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import { green } from '@material-ui/core/colors';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Divider,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  productCard: {
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  productInfo: {
    flexGrow: 1,
  },
  productPrice: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  orderSummary: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  totalPrice: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  categoryChip: {
    margin: theme.spacing(0.5),
  },
  btnWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  orderItem: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  scheduleChoiceDialog: {
    '& .MuiDialog-paper': {
      minWidth: 400,
    },
  },
}));

/**
 * Verifica se um item (serviço) exige agendamento antes da venda.
 * Serviços com email ou schedule=true requerem agendamento.
 */
const serviceRequiresSchedule = item =>
  item.type === 'service' &&
  (Boolean(item.email && item.email.trim()) || Boolean(item.schedule));

const OrderModal = ({
  open,
  onClose,
  contactId,
  ticketId,
  dealId: dealIdProp,
  reload,
  onSaleSuccess,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [items, setItems] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [initialOrderItems, setInitialOrderItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  // Modal de escolha de agendamentos ao PROCESSAR venda (não ao editar carrinho)
  const [scheduleChoiceModal, setScheduleChoiceModal] = useState({
    open: false,
    item: null,
    action: null,
    selectedScheduleIds: [],
    pendingSubmit: null, // { saleData, servicesRequiringSchedule }
    itemsToCancel: [], // [{ item, countToCancel }]
    currentIndex: 0,
  });

  const categories = [
    { key: 'all', label: i18n.t('orderModal.categories.all') },
    { key: 'product', label: i18n.t('orderModal.categories.products') },
    { key: 'service', label: i18n.t('orderModal.categories.services') },
  ];

  // Carregar venda atual do deal quando o modal abre (para edição)
  // Usa GET /deals/:id/sale-with-schedules para obter itens com agendamentos vinculados
  const loadDealSale = async dealId => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/deals/${dealId}/sale-with-schedules`);
      const saleItems = data?.items || [];
      if (saleItems.length > 0) {
        const mapped = saleItems.map(item => {
          const qty = item.quantity || 1;
          const unitVal = item.unitValue ?? 0;
          const schedules = (item.schedules || []).filter(
            s => s.status !== 'cancelled'
          );
          return {
            id: item.productId,
            type: item.type || 'product',
            quantity: qty,
            value: unitVal,
            name: item.name || '',
            email: item.email,
            schedule: item.schedule,
            subtotal: qty * unitVal,
            schedules,
          };
        });
        setOrderItems(mapped);
        setInitialOrderItems(mapped);
      } else {
        setOrderItems([]);
        setInitialOrderItems([]);
      }
    } catch (err) {
      console.warn(
        'Erro ao carregar venda do deal (usando fallback /sale):',
        err
      );
      try {
        const { data } = await api.get(`/deals/${dealId}/sale`);
        const saleItems = data?.items || [];
        if (saleItems.length > 0) {
          const mapped = saleItems.map(item => ({
            id: item.productId,
            type: item.type || 'product',
            quantity: item.quantity || 1,
            value: item.unitValue ?? 0,
            name: item.name || '',
            subtotal: (item.quantity || 1) * (item.unitValue ?? 0),
            schedules: [],
          }));
          setOrderItems(mapped);
          setInitialOrderItems(mapped);
        } else {
          setOrderItems([]);
          setInitialOrderItems([]);
        }
      } catch (fallbackErr) {
        setOrderItems([]);
        setInitialOrderItems([]);
      }
    }
  };

  useEffect(() => {
    if (open) {
      loadProductsAndServices();
      if (dealIdProp) {
        loadDealSale(dealIdProp);
      } else {
        setOrderItems([]);
        setInitialOrderItems([]);
      }
    }
  }, [open, dealIdProp]);

  // Verifica se o carrinho tem alterações em relação à venda carregada (modo edição)
  const hasCartChanges = useMemo(() => {
    if (initialOrderItems.length === 0) {
      return orderItems.length > 0;
    }
    if (orderItems.length !== initialOrderItems.length) return true;

    const toMap = items => {
      const m = new Map();
      for (const it of items) {
        const key = `${it.id}|${it.type || 'product'}`;
        m.set(key, (m.get(key) || 0) + (it.quantity || 1));
      }
      return m;
    };
    const currentMap = toMap(orderItems);
    const initialMap = toMap(initialOrderItems);

    if (currentMap.size !== initialMap.size) return true;
    for (const [key, qty] of currentMap) {
      if ((initialMap.get(key) || 0) !== qty) return true;
    }
    for (const [key, qty] of initialMap) {
      if ((currentMap.get(key) || 0) !== qty) return true;
    }
    return false;
  }, [orderItems, initialOrderItems]);

  const canSubmitOrder =
    orderItems.length > 0 && (!dealIdProp || hasCartChanges);

  const loadProductsAndServices = async () => {
    try {
      setLoading(true);
      const { companyId } = user;

      const { data: itemsData } = await api.get(
        `/products/list?searchParam=${searchTerm}`,
        {
          params: { companyId },
        }
      );

      setItems(itemsData.products || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setOrderItems([]);
    setInitialOrderItems([]);
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const updateItemQuantity = (itemId, type, newQuantity) => {
    if (newQuantity <= 0) {
      removeItemFromOrder(itemId, type);
      return;
    }

    const updatedItems = orderItems.map(i => {
      if (i.id === itemId && i.type === type) {
        return {
          ...i,
          quantity: newQuantity,
          subtotal: (i.value || 0) * newQuantity,
        };
      }
      return i;
    });

    setOrderItems(updatedItems);
  };

  const addItemToOrder = (item, type) => {
    const existingItemIndex = orderItems.findIndex(
      orderItem => orderItem.id === item.id && orderItem.type === type
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...orderItems];
      updatedItems[existingItemIndex].quantity += 1;
      updateItemQuantity(
        updatedItems[existingItemIndex].id,
        updatedItems[existingItemIndex].type,
        updatedItems[existingItemIndex].quantity
      );
    } else {
      const newItem = {
        ...item,
        type,
        quantity: 1,
        subtotal: item.value || 0,
        schedules: [],
      };
      setOrderItems([...orderItems, newItem]);
    }

    toast.success(
      i18n.t('orderModal.toasts.itemAdded', { itemName: item.name })
    );
  };

  const formatScheduleDate = dateStr => {
    if (!dateStr) return '-';
    try {
      return moment(dateStr).format('DD/MM/YYYY HH:mm');
    } catch {
      return '-';
    }
  };

  const cancelSchedulesByIds = async scheduleIds => {
    for (const id of scheduleIds) {
      try {
        await api.patch(`/calendar/${id}`, { status: 'cancelled' });
      } catch (err) {
        console.warn('Erro ao cancelar agendamento:', err);
        toastError(err);
        throw err;
      }
    }
  };

  const handleConfirmScheduleChoice = async () => {
    const {
      item,
      action,
      selectedScheduleIds,
      pendingSubmit,
      itemsToCancel,
      currentIndex,
    } = scheduleChoiceModal;

    if (!item) return;

    const scheduleIdsToCancel =
      action === 'cancel_all'
        ? (item.schedules || []).map(s => s.id)
        : selectedScheduleIds;

    const { countToCancel } = itemsToCancel?.[currentIndex] || {};
    const minRequired = countToCancel ?? 0;

    if (minRequired > 0 && scheduleIdsToCancel.length < minRequired) {
      toast.warning(i18n.t('orderModal.scheduleChoice.selectOneRequired'));
      return;
    }

    if (scheduleIdsToCancel.length > 0) {
      try {
        await cancelSchedulesByIds(scheduleIdsToCancel);
        toast.success(i18n.t('orderModal.scheduleChoice.cancelled'));
      } catch {
        setScheduleChoiceModal(prev => ({ ...prev, open: false }));
        return;
      }
    }

    if (!pendingSubmit || !itemsToCancel) {
      setScheduleChoiceModal({
        open: false,
        item: null,
        action: null,
        selectedScheduleIds: [],
        pendingSubmit: null,
        itemsToCancel: [],
        currentIndex: 0,
      });
      return;
    }

    const nextIndex = currentIndex + 1;
    if (nextIndex < itemsToCancel.length) {
      const next = itemsToCancel[nextIndex];
      setScheduleChoiceModal({
        open: true,
        item: next.item,
        action:
          next.countToCancel >= (next.item.schedules || []).length
            ? 'remove'
            : 'reduce',
        selectedScheduleIds: [],
        pendingSubmit,
        itemsToCancel,
        currentIndex: nextIndex,
      });
      return;
    }

    setScheduleChoiceModal({
      open: false,
      item: null,
      action: null,
      selectedScheduleIds: [],
      pendingSubmit: null,
      itemsToCancel: [],
      currentIndex: 0,
    });

    try {
      await proceedWithSale(
        pendingSubmit.saleData,
        pendingSubmit.servicesRequiringSchedule || []
      );
    } catch (err) {
      toastError(err);
    }
  };

  const removeItemFromOrder = (itemId, type) => {
    const updatedItems = orderItems.filter(
      i => !(i.id === itemId && i.type === type)
    );
    setOrderItems(updatedItems);
  };

  const calculateTotal = () => {
    return orderItems.reduce((total, item) => total + +item.subtotal, 0);
  };

  const formatPrice = price => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price || 0);
  };

  /**
   * Executa a venda (chamada à API e feedback).
   * Usado diretamente ou passado para onOpenScheduleBeforeSale quando há serviços que exigem agendamento.
   */
  const performSale = async saleData => {
    const { data: responseData } = await api.post(
      '/deals/process-sale',
      saleData
    );

    if (responseData?.scheduleInfo?.cancelledSchedules?.count > 0) {
      toast.warning(
        'Agendamento anterior foi cancelado, favor realizar um novo agendamento'
      );
    }

    toast.success(i18n.t('orderModal.toasts.orderCreated'));

    if (typeof reload === 'function') {
      reload();
    }

    handleClose();
  };

  const proceedWithSale = async (saleData, servicesRequiringSchedule) => {
    await performSale(saleData);
    if (
      servicesRequiringSchedule.length > 0 &&
      typeof onSaleSuccess === 'function'
    ) {
      onSaleSuccess(servicesRequiringSchedule);
    }
  };

  const handleSaveOrder = async values => {
    try {
      if (!dealIdProp && !ticketId) {
        throw new Error('Ticket ID é obrigatório para processar a venda');
      }
      if (orderItems.length === 0) {
        throw new Error('Adicione pelo menos um item ao pedido');
      }

      let dealId = dealIdProp;
      if (!dealId && ticketId) {
        const { data: ticketData } = await api.get(`/tickets/${ticketId}`);
        if (ticketData?.deals?.length > 0) {
          if (ticketData.status === 'open') {
            const currentDeal = ticketData.deals.reduce((latest, current) =>
              (current?.id || 0) > (latest?.id || 0) ? current : latest
            );
            dealId =
              currentDeal?.id ?? ticketData.dealId ?? ticketData.deal?.id;
          } else {
            dealId =
              ticketData.dealId ??
              ticketData.deal?.id ??
              ticketData.deals[0]?.id;
          }
        }
      }
      if (!dealId) {
        throw new Error('Deal não encontrado para este ticket');
      }

      const saleData = {
        dealId,
        items: orderItems.map(item => ({
          productId: item.id,
          quantity: item.quantity,
        })),
        status: 'sold',
        totalValue: +calculateTotal(),
      };

      const servicesRequiringSchedule = orderItems.filter(
        serviceRequiresSchedule
      );

      // Edição: apenas serviços/unidades NOVOS precisam de agendamento (os que já existiam já têm)
      const servicesNeedingNewSchedule =
        dealIdProp && initialOrderItems.length > 0
          ? servicesRequiringSchedule
              .map(svc => {
                const initial = initialOrderItems.find(
                  i => i.id === svc.id && (i.type || 'product') === (svc.type || 'product')
                );
                const initialQty = initial?.quantity || 0;
                const currentQty = svc.quantity || 0;
                const delta = Math.max(0, currentQty - initialQty);
                if (delta === 0) return null;
                return { ...svc, quantity: delta };
              })
              .filter(Boolean)
          : servicesRequiringSchedule;

      // Edição de venda: comparar com estado inicial para ver se há agendamentos a cancelar
      if (dealIdProp && initialOrderItems.length > 0) {
        const itemsToCancel = [];
        for (const initialItem of initialOrderItems) {
          const schedules = (initialItem.schedules || []).filter(
            s => s.status !== 'cancelled'
          );
          if (schedules.length === 0) continue;

          const currentItem = orderItems.find(
            i => i.id === initialItem.id && i.type === initialItem.type
          );
          const currentQty = currentItem ? currentItem.quantity : 0;
          const initialQty = initialItem.quantity || 0;

          if (currentQty < initialQty) {
            const countToCancel = initialQty - currentQty;
            itemsToCancel.push({
              item: { ...initialItem, schedules },
              countToCancel,
            });
          }
        }

        if (itemsToCancel.length > 0) {
          setScheduleChoiceModal({
            open: true,
            item: itemsToCancel[0].item,
            action: itemsToCancel[0].countToCancel >= itemsToCancel[0].item.schedules?.length ? 'remove' : 'reduce',
            selectedScheduleIds: [],
            pendingSubmit: { saleData, servicesRequiringSchedule: servicesNeedingNewSchedule },
            itemsToCancel,
            currentIndex: 0,
          });
          return;
        }
      }

      await proceedWithSale(saleData, servicesNeedingNewSchedule);
    } catch (err) {
      toastError(err);
    }
  };

  const filteredItems = () => {
    let filteredItems = items;

    // Filtrar por categoria
    if (selectedCategory !== 'all') {
      filteredItems = filteredItems.filter(
        item => item.type === selectedCategory
      );
    }

    // Filtrar por termo de busca
    if (searchTerm) {
      filteredItems = filteredItems.filter(
        item =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.description &&
            item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filteredItems;
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        scroll='paper'
      >
        <DialogTitle>
          <Box display='flex' alignItems='center'>
            <ShoppingCartIcon style={{ marginRight: 8 }} />
            {i18n.t('orderModal.title')}
          </Box>
        </DialogTitle>

        <Formik
          initialValues={{ contactId, items: [] }}
          onSubmit={handleSaveOrder}
        >
          {({ isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                {/* Campo de busca */}
                <TextField
                  className={classes.searchField}
                  fullWidth
                  label={i18n.t('orderModal.search.label')}
                  variant='outlined'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder={i18n.t('orderModal.search.placeholder')}
                />

                {/* Filtros por categoria */}
                <Box marginBottom={2}>
                  {categories.map(category => (
                    <Chip
                      key={category.key}
                      label={category.label}
                      className={classes.categoryChip}
                      color={
                        selectedCategory === category.key
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => setSelectedCategory(category.key)}
                      variant={
                        selectedCategory === category.key
                          ? 'default'
                          : 'outlined'
                      }
                    />
                  ))}
                </Box>

                <Grid container spacing={2}>
                  {/* Lista de produtos/serviços */}
                  <Grid item xs={12} md={8}>
                    <Typography variant='h6' gutterBottom>
                      {i18n.t('orderModal.availableItems.title')}
                    </Typography>

                    {loading ? (
                      <Box display='flex' justifyContent='center' p={4}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Box maxHeight={400} overflow='auto'>
                        {filteredItems().map(item => (
                          <Card
                            key={`${item.type}-${item.id}`}
                            className={classes.productCard}
                          >
                            <CardContent>
                              <Grid container alignItems='center'>
                                <Grid item className={classes.productInfo}>
                                  <Typography
                                    variant='subtitle1'
                                    component='h3'
                                  >
                                    {item.name}
                                    <Chip
                                      size='small'
                                      label={
                                        item.type === 'product'
                                          ? i18n.t(
                                              'orderModal.itemTypes.product'
                                            )
                                          : i18n.t(
                                              'orderModal.itemTypes.service'
                                            )
                                      }
                                      color={
                                        item.type === 'product'
                                          ? 'primary'
                                          : 'secondary'
                                      }
                                      style={{ marginLeft: 8 }}
                                    />
                                  </Typography>
                                  {item.description && (
                                    <Typography
                                      variant='body2'
                                      color='textSecondary'
                                    >
                                      {item.description}
                                    </Typography>
                                  )}
                                  {item.stock && item.type === 'product' && (
                                    <Typography
                                      variant='caption'
                                      color='textSecondary'
                                    >
                                      {i18n.t('orderModal.stock', {
                                        stock: item.stock,
                                      })}
                                    </Typography>
                                  )}
                                  {item.duration && item.type === 'service' && (
                                    <Typography
                                      variant='caption'
                                      color='textSecondary'
                                    >
                                      {i18n.t('orderModal.duration', {
                                        duration: item.duration,
                                      })}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant='h6'
                                    className={classes.productPrice}
                                  >
                                    {formatPrice(item.value)}
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      addItemToOrder(item, item.type)
                                    }
                                  >
                                    {i18n.t('orderModal.buttons.add')}
                                  </Button>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}

                        {filteredItems().length === 0 && (
                          <Box textAlign='center' p={4}>
                            <Typography color='textSecondary'>
                              {i18n.t('orderModal.noItemsFound')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {/* Resumo do pedido */}
                  <Grid item xs={12} md={4}>
                    <Typography variant='h6' gutterBottom>
                      {i18n.t('orderModal.orderSummary.title', {
                        count: orderItems.length,
                      })}
                    </Typography>

                    <List>
                      {orderItems.map((item, index) => (
                        <ListItem
                          key={`${item.type}-${item.id}-${index}`}
                          className={classes.orderItem}
                        >
                          <ListItemText
                            primary={item.name}
                            secondary={
                              <>
                                <Typography component='span' variant='body2'>
                                  {formatPrice(item.value)} x {item.quantity}
                                </Typography>
                                <br />
                                <Typography
                                  component='span'
                                  variant='body2'
                                  color='primary'
                                >
                                  {i18n.t('orderModal.orderSummary.subtotal')}:{' '}
                                  {formatPrice(item.subtotal)}
                                </Typography>
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box className={classes.quantityControl}>
                              <IconButton
                                size='small'
                                onClick={() =>
                                  updateItemQuantity(
                                    item.id,
                                    item.type,
                                    item.quantity - 1
                                  )
                                }
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Typography>{item.quantity}</Typography>
                              <IconButton
                                size='small'
                                onClick={() =>
                                  updateItemQuantity(
                                    item.id,
                                    item.type,
                                    item.quantity + 1
                                  )
                                }
                              >
                                <AddIcon />
                              </IconButton>
                              <IconButton
                                size='small'
                                color='secondary'
                                onClick={() =>
                                  removeItemFromOrder(item.id, item.type)
                                }
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>

                    {orderItems.length === 0 && (
                      <Box textAlign='center' p={2}>
                        <Typography color='textSecondary'>
                          {i18n.t('orderModal.orderSummary.empty')}
                        </Typography>
                      </Box>
                    )}

                    {orderItems.length > 0 && (
                      <>
                        <Divider />
                        <Box className={classes.orderSummary}>
                          <Typography
                            variant='h6'
                            className={classes.totalPrice}
                          >
                            {i18n.t('orderModal.orderSummary.total')}:{' '}
                            {formatPrice(calculateTotal())}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color='secondary'
                  disabled={isSubmitting}
                  variant='outlined'
                >
                  {i18n.t('orderModal.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  color='primary'
                  disabled={isSubmitting || !canSubmitOrder}
                  variant='contained'
                  className={classes.btnWrapper}
                >
                  {i18n.t('orderModal.buttons.confirm')}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Modal de escolha de agendamento(s) ao remover/reduzir item */}
      <Dialog
        open={scheduleChoiceModal.open}
        onClose={() =>
          setScheduleChoiceModal({
            open: false,
            item: null,
            action: null,
            selectedScheduleIds: [],
            pendingSubmit: null,
            itemsToCancel: [],
            currentIndex: 0,
          })
        }
        className={classes.scheduleChoiceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t('orderModal.scheduleChoice.beforeProcessTitle')}
        </DialogTitle>
        <DialogContent dividers>
          {scheduleChoiceModal.item && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {scheduleChoiceModal.action === 'remove'
                  ? `${scheduleChoiceModal.item.name}: item removido da venda. Selecione qual(is) agendamento(s) cancelar.`
                  : `${scheduleChoiceModal.item.name}: quantidade reduzida. Selecione qual(is) agendamento(s) cancelar (${scheduleChoiceModal.itemsToCancel?.[scheduleChoiceModal.currentIndex]?.countToCancel ?? 0} necessário(s)).`}
              </Typography>
              {scheduleChoiceModal.action === 'remove' && (
                <Button
                  size="small"
                  onClick={() =>
                    setScheduleChoiceModal(s => ({
                      ...s,
                      selectedScheduleIds: (s.item?.schedules || []).map(
                        sc => sc.id
                      ),
                    }))
                  }
                  style={{ marginBottom: 8 }}
                >
                  {i18n.t('orderModal.scheduleChoice.selectAll')}
                </Button>
              )}
              <Box mt={2}>
                {(scheduleChoiceModal.item.schedules || []).map(schedule => (
                  <Box
                    key={schedule.id}
                    display="flex"
                    alignItems="center"
                    style={{
                      padding: 8,
                      marginBottom: 8,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 4,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={scheduleChoiceModal.selectedScheduleIds.includes(
                            schedule.id
                          )}
                          onChange={e => {
                            const id = schedule.id;
                            const prev =
                              scheduleChoiceModal.selectedScheduleIds;
                            const next = e.target.checked
                              ? [...prev, id]
                              : prev.filter(x => x !== id);
                            setScheduleChoiceModal(s => ({
                              ...s,
                              selectedScheduleIds: next,
                            }));
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {schedule.title || schedule.productName || 'Agendamento'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatScheduleDate(schedule.startDateTime)}
                            {schedule.location ? ` · ${schedule.location}` : ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setScheduleChoiceModal({
                open: false,
                item: null,
                action: null,
                selectedScheduleIds: [],
              })
            }
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmScheduleChoice}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderModal;
